/**
 * Returns all nav items in the tree that have a subnav as a linear list.
 * @param {Array} navTree A standard array of navitems supplied to a nav component.
 * @returns An array of navitems.
 */
function getSubnavs(navTree) {
	return navTree.reduce((acc, navitem) => {
		if ((navitem.currentWhen || navitem.url) && navitem.subnav) {
			acc.push(navitem);
			acc.push(...getSubnavs(navitem.subnav));
		}
		return acc;
	}, []);
}

/**
 * Finds an appropriate list of navitems(subnav) for the supplied value of current.
 * Current is supplied as the path segment of a url. The value of current is tested
 * against each navitem with a subnav using isCurrent().
 * @param {Array} navTree A standard array of navitems supplied to a nav component.
 * @param {String} current A path segment of a url.
 * @param {Boolean} rootAsDefault if true, returns the root array if no match is found else [].
 * @returns An array of menu items to display for the value of current.
 */
export function getSubnav(navTree, current, rootAsDefault = false) {
	let subnavs = getSubnavs(navTree).filter(item => isCurrentSubnav(item, current)).reverse();
	return (subnavs.length && subnavs[0])
		? ({ parent: subnavs[0], items: subnavs[0].subnav })
		: rootAsDefault
			? ({ parent: subnavs[0], items: navTree })
			: {};
}

/**
 * Returns true if the supplied navItem should be considered current based on the
 * supplied value of current.
 *
 * The navItem's currentWhen value will be checked first. It should be defined as
 * a regular expression that is tested against the value of current.
 *
 * If no currentWhen value exists, current will be checked against the explicit
 * value of the navitem's url.
 *
 * @param {Object} navItem The nav item to test.
 * @param {String} current A path segment of a url.
 */
export function isCurrent(navItem, current) {
	if (navItem.currentWhen || navItem.url) {
		try {
			let pattern = navItem.currentWhen
						|| (
							navItem.url.match('/$')
								? `^${navItem.url}?.*$`
								: `^${navItem.url}/?.*$`
						);
			return new RegExp(pattern).test(current);
		}
		catch (e) {
			console.error('Mobile Header, Bad navigation configuration', e);
		}
	}
	return false;
}

/**
 * Returns true if the supplied navItem should be considered the current subnav based on the
 * supplied value of current.
 *
 * The navItem's subnavWhen value will be checked first. It should be defined as
 * a regular expression that is tested against the value of current.
 *
 * If no subnavWhen value exists, current will be checked against the explicit
 * value of the navitem's url.
 *
 * @param {Object} navItem The nav item to test.
 * @param {String} current A path segment of a url.
 */
export function isCurrentSubnav(navItem, current) {
	if (navItem.subnavWhen || navItem.url) {
		try {
			let pattern = navItem.subnavWhen
						|| (
							navItem.url.match('/$')
								? `^${navItem.url}.+$`
								: `^${navItem.url}/.+$`
						);
			return new RegExp(pattern).test(current);
		}
		catch (e) {
			console.error('Mobile Header, Bad navigation configuration', e);
		}
	}
	return false;
}

export function currentSkinTier(skin) {
	return (skin && skin.length) ? skin[0] : {};
}

export function nextSkinTiers(skin) {
	let nextskins = skin ? [...skin] : [];
	if (nextskins.length > 1) {
		nextskins.shift();
	}
	else if (nextskins.length && !nextskins[0].cascade) {
		nextskins = null;
	}
	return nextskins;
}